import { useState } from 'react';
import cn from 'classnames';
import Button from '@creditflowresearch/uilib/Button';
import Tooltip from '@creditflowresearch/uilib/Tooltip';
import Menu, { MenuItem, MenuItemType, MenuDivider } from '@creditflowresearch/uilib/Menu';
import IconArrowDownBold from '@creditflowresearch/uilib/Icon/IconArrowDownBold';
import IconCheck from '@creditflowresearch/uilib/Icon/IconCheck';
import IconPlus from '@creditflowresearch/uilib/Icon/IconPlus';
import TruncateText from '@creditflowresearch/uilib/TruncateText';
import { BOND_SCREENER_VIEWS_LIMIT_MESSAGE } from '@/creditflowresearch/application/BondScreener/Sidebar/bondScreenerViewsConstants';

import styles from './BondScreenerSelectViewButton.module.scss';

export interface IBondScreenerViewOption {
  value: string,
  label: string,
}

interface IBondScreenerSelectViewButtonProps {
  dataTest: string,
  views: IBondScreenerViewOption[],
  activeViewId: string | null,
  onSelectView: (id: string | null) => void,
  onCreateView?: () => void,
  canCreateView?: boolean,
  disabled?: boolean,
}

const BondScreenerSelectViewButton = ({
  dataTest,
  views,
  activeViewId,
  onSelectView,
  onCreateView,
  canCreateView = false,
  disabled = false,
}: IBondScreenerSelectViewButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const buttonLabel = views.find((view) => view.value === activeViewId)?.label ?? views[0].label;

  const handleSelect = (id: string | null): void => {
    onSelectView(id);
    setIsOpen(false);
  };

  return (
    <Menu
      trigger={ ({ referenceProps }) => (
        <Button
          { ...referenceProps }
          dataTest={ dataTest }
          variant="outline"
          size="S"
          className={ cn(styles.button, { [styles.active]: isOpen }) }
          childrenClassName={ styles.buttonLabelWrapper }
          icon={ (
            <IconArrowDownBold
              className={ cn(styles.buttonIcon, { [styles.active]: isOpen, [styles.disabled]: disabled }) }
            />
          ) }
          iconPosition="right"
          disabled={ disabled }
        >
          <TruncateText value={ buttonLabel } />
        </Button>
      ) }
      placement="bottom-end"
      open={ isOpen }
      setOpen={ setIsOpen }
      usePortal={ false }
      dataTest={ `${ dataTest }Dropdown` }
      contentClassName={ styles.dropdownContent }
      disabled={ disabled }
      returnFocusOnClose
      initialFocus="firstItem"
    >
      { views.map((view) => {
        const isActive = activeViewId === view.value;

        return (
          <MenuItem
            key={ view.value }
            className={ cn(styles.dropdownItem, { [styles.active]: isActive }) }
            iconWrapperClassName={ styles.iconWrapper }
            textClassName={ styles.textWrapper }
            type={ MenuItemType.button }
            dataTest={ `${ dataTest }View-${ view.value }` }
            icon={ isActive ? IconCheck : undefined }
            onClick={ () => handleSelect(view.value) }
          >
            <TruncateText value={ view.label } />
          </MenuItem>
        );
      }) }
      <MenuDivider />
      <Tooltip
        content={ BOND_SCREENER_VIEWS_LIMIT_MESSAGE }
        disabled={ canCreateView }
        wrpClassName={ styles.createViewTooltip }
        containerClassName={ styles.createViewTooltip }
      >
        <MenuItem
          type={ MenuItemType.button }
          className={ cn(styles.dropdownItem, styles.active) }
          iconWrapperClassName={ styles.iconWrapper }
          textClassName={ styles.textWrapper }
          dataTest={ `${ dataTest }CreateNewView` }
          disabled={ !canCreateView }
          onClick={ onCreateView }
          icon={ IconPlus }
        >
          Create New View
        </MenuItem>
      </Tooltip>
    </Menu>
  );
};

export default BondScreenerSelectViewButton;
